import { getDropboxErrorCode, getDropboxErrorKey } from '@/lib/dropbox/errors';
import { getShareImportErrorKey, isShareImportPlatform, isShareImportProRequired } from '@/lib/shareImport/shareImport';
import { unsupportedSourceDescriptionKey } from '@/lib/frameio/unsupportedSourceDescriptionKey';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FileText } from "lucide-react";
import UploadModal from "@/components/UploadModal";
import LibraryCreateHero from "@/components/library/LibraryCreateHero";
import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MyProject, useMyProjectsQuery } from "@/generated/graphql";
import { useWindowScroll } from "@uidotdev/usehooks";
import { ProjectCard } from "@/components/ProjectCard";
import { useUploadFile, UploadProgress } from "@/hooks/use-upload-file";
import { useToast } from "@/hooks/use-toast";
import { VideoMetadata, getVideoMetadata, detectVideoPlatform } from "@/lib/video-metadata";
import { parseVideoDeepLink } from "@/lib/content/parseVideoDeepLink";
import { resolveLibrarySourceAccess } from "@/lib/library/resolveLibrarySourceAccess";
import { validateUrlVideoMetadata } from "@/lib/upload/validateUrlVideoMetadata";
import { MAX_VIDEO_DURATION_SECONDS } from "@/lib/video-limits";
import { useHasFeature } from "@/lib/useHasFeature";
import NewsPdfUploadModal from "@/components/NewsPdfUploadModal";
import DuplicateProjectDialog from "@/components/DuplicateProjectDialog";
import { useUserRole } from "@/hooks/use-user-role";
import { trackEvent } from "@/lib/analytics/trackEvent";
import { useQuotaUpsellDialog } from "@/hooks/use-quota-upsell-dialog";
import { detectAudioTrack } from "@/lib/media/detectAudioTrack";

const ProjectsListPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [videoUrl, setVideoUrl] = useState("");
  const PAGE_SIZE = 12;
  const { data, loading, error, fetchMore } = useMyProjectsQuery({
    variables: { limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });
  const hasProFeatures = useHasFeature('pro_features');
  const hasNewsPdfFeature = useHasFeature('news-pdf');
  const { permissions } = useUserRole();
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const [scroll] = useWindowScroll();
  const scrollY = scroll?.y ?? 0;

  // News PDF upload modal state
  const [isNewsPdfModalOpen, setIsNewsPdfModalOpen] = useState(false);

  // Duplicate project dialog state
  const [duplicateProject, setDuplicateProject] = useState<MyProject | null>(null);

  // File upload functionality
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getUploadUrl, error: uploadError, createBlobUrl, revokeBlobUrl, getVideoDuration, abortUpload } = useUploadFile();
  const { toast } = useToast();
  const { dialog: quotaUpsellDialog, openQuotaUpsell } = useQuotaUpsellDialog();

  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  // URL-based video metadata handling
  const [urlVideoMetadata, setUrlVideoMetadata] = useState<VideoMetadata | null>(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);

  const projects = data?.myProjects ?? [];
  const isFirstClip = projects.length === 0;

  // Handle file selection - immediately create blob URL, get duration, start upload, and show UploadModal
  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type immediately
    if (file.type !== 'video/mp4') {
      toast({
        title: t('errors.invalidFileType'),
        description: t('errors.onlyMp4Accepted'),
        variant: "destructive",
      });
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Get video duration before proceeding
    let duration: number | null = null;
    try {
      duration = await getVideoDuration(file);
    } catch (error) {
      console.error('Error getting video duration:', error);
    }
    if (!duration) {
      toast({
        title: t('errors.failedToReadVideo'),
        description: t('errors.couldNotDetermineDuration'),
        variant: "destructive",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // A video without audio cannot be transcribed, so processing would fail
    if (await detectAudioTrack(file) === 'absent') {
      toast({
        title: t('errors.videoHasNoAudio'),
        description: t('errors.videoHasNoAudioDescription'),
        variant: "destructive",
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Reset states
    setUploadProgress(null);
    setIsUploadComplete(false);
    setUploadedVideoUrl(null);
    setVideoDuration(0);

    // Create blob URL for immediate preview
    const newBlobUrl = createBlobUrl(file);
    setBlobUrl(newBlobUrl);
    setSelectedFile(file);
    setIsUploadModalOpen(true);

    // Save validated duration
    setVideoDuration(duration);

    // Start upload immediately
    startUpload(file);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

    // Start upload process (called immediately when file is selected)
  const startUpload = async (file: File) => {
    try {
      setUploadProgress({ percentage: 0, uploadedBytes: 0, totalBytes: file.size, uploadSpeed: 0, remainingTime: 0 });

      const uploadUrl = await getUploadUrl(file, (progress) => {
        setUploadProgress(progress);
      });

      if (uploadUrl) {
        setUploadedVideoUrl(uploadUrl);
        setIsUploadComplete(true);
        setUploadProgress(null);
        setIsUploaded(true);
        trackEvent("video_uploaded", {
          is_first: (data?.myProjects?.length ?? 0) === 0,
        });
      }
    } catch (error) {
      // Check if error is due to abort - don't show error toast for intentional cancellation
      if (error instanceof Error && error.message === 'Upload was aborted') {
        console.log('Upload was aborted by user');
      } else {
        toast({
          title: t('errors.uploadError'),
          description: uploadError || t('errors.uploadErrorOccurred'),
          variant: "destructive",
        });
      }
      setUploadProgress(null);
      setIsUploadComplete(false);
    }
  };

  // Handle project creation (called when user clicks create project button)
  const handleCreateProject = () => {
    if (urlVideoMetadata) {
      // Handle URL-based upload completion
      setUrlVideoMetadata(null);
      setVideoUrl(""); // Clear the URL input
    } else {
      // Handle file-based upload completion
      setIsUploadModalOpen(false);
      cleanupBlobUrl();
    }
  };

  // Clean up blob URL and reset states
  const cleanupBlobUrl = () => {
    if (blobUrl) {
      revokeBlobUrl(blobUrl);
      setBlobUrl(null);
    }
    setSelectedFile(null);
    setUploadProgress(null);
    setUploadedVideoUrl(null);
    setIsUploadComplete(false);
    setVideoDuration(0);
  };

  // Handle modal close
  const handleModalClose = () => {
    if (urlVideoMetadata) {
      // Handle URL-based modal close
      setUrlVideoMetadata(null);
      setVideoUrl(""); // Clear the URL input
    } else {
      // Handle file-based modal close
      // Abort upload if in progress
      if (uploadProgress && !isUploadComplete) {
        abortUpload();
      }

      setIsUploadModalOpen(false);
      cleanupBlobUrl();
    }
  };

  // Handle file selection button click
  const handleFileSelectionClick = () => {
    if (!hasProFeatures) {
      openQuotaUpsell('paid_feature');
      return;
    }
    fileInputRef.current?.click();
  };

  // Handle URL upload
  const handleUrlUpload = async (videoUrl: string) => {
    if (!videoUrl) return;

    const platform = detectVideoPlatform(videoUrl);
    const access = resolveLibrarySourceAccess({ platform, hasProAccess: hasProFeatures });
    if (access === 'unsupported') {
      toast({
        title: t('library.unsupportedSource'),
        description: t(unsupportedSourceDescriptionKey({ url: videoUrl, platform })),
        variant: "destructive",
      });
      return;
    }
    if (access === 'pro_required') {
      openQuotaUpsell('paid_feature');
      return;
    }

    setIsLoadingMetadata(true);
    try {
      const metadata = await getVideoMetadata(videoUrl);
      const invalid = validateUrlVideoMetadata(metadata, { maxDurationSeconds: MAX_VIDEO_DURATION_SECONDS });
      if (invalid) {
        toast({
          title: t(invalid.titleKey),
          description: t(invalid.descriptionKey, invalid.descriptionParams),
          variant: "destructive",
        });
        return;
      }
      setUrlVideoMetadata(metadata);
      setIsUploaded(false);
    } catch (error) {
      if (platform === 'dropbox') {
        if (getDropboxErrorCode(error) === 'DROPBOX_PRO_REQUIRED') openQuotaUpsell('paid_feature');
        else toast({ title: t('errors.videoAnalysisError'), description: t(getDropboxErrorKey(error)), variant: 'destructive' });
        return;
      }
      if (isShareImportPlatform(platform)) {
        if (isShareImportProRequired(error)) openQuotaUpsell('paid_feature');
        else toast({ title: t('errors.videoAnalysisError'), description: t(getShareImportErrorKey(error, platform)), variant: 'destructive' });
        return;
      }
      console.error('Failed to get video metadata:', error);
      toast({
        title: t('errors.videoAnalysisError'),
        description: t('errors.checkVideoLink'),
        variant: "destructive",
      });
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  // Deep link from the AutoClipper Chrome extension ("Send to AutoClipper"
  // on YouTube): /projects?video=<url> starts the URL flow automatically.
  useEffect(() => {
    const deepLink = parseVideoDeepLink(window.location.search);
    if (!deepLink) return;
    searchParams.delete('video');
    setSearchParams(searchParams, { replace: true });
    setVideoUrl(deepLink);
    void handleUrlUpload(deepLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = () => {
    const url = videoUrl.trim();
    if (!url) {
      toast({
        title: t('library.urlRequired'),
        description: t('library.urlRequiredDescription'),
      });
      return;
    }
    void handleUrlUpload(url);
  };

  const handleLoadMore = async () => {
    if (isFetchingMoreRef.current || loading || error || !hasMore) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      const prevCount = projects.length;
      const res = await fetchMore({
        variables: {
          limit: PAGE_SIZE,
          offset: prevCount,
        },
      });
      const fetched = res.data?.myProjects?.length ?? 0;
      if (fetched < PAGE_SIZE) setHasMore(false);
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  };

  // Trigger load more as the user scrolls near the bottom of the page
  useEffect(() => {
    if (loading || !hasMore) return;
    const currentY = scrollY ?? 0;
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const docH = document.documentElement.scrollHeight || document.body.scrollHeight;
    const distanceFromBottom = docH - (currentY + viewportH);
    if (distanceFromBottom < 600) {
      handleLoadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY, hasMore, loading]);

  // If the initial page contains fewer than PAGE_SIZE items, we already reached the end
  useEffect(() => {
    if (!loading && projects.length > 0 && projects.length < PAGE_SIZE) {
      setHasMore(false);
    }

  }, [loading, projects.length]);

  // Cleanup blob URL and abort upload on unmount
  useEffect(() => {
    return () => {
      if (blobUrl) {
        revokeBlobUrl(blobUrl);
      }
    };
  }, [blobUrl, revokeBlobUrl]);

  // The marketing site hands off a pasted link via ?url=. Prefill the input and
  // open the create modal pre-loaded, then drop the param so a refresh or
  // back-navigation doesn't re-trigger it.
  const urlParamConsumed = useRef(false);
  useEffect(() => {
    if (urlParamConsumed.current) return;
    const incomingUrl = searchParams.get("url");
    if (!incomingUrl) return;
    urlParamConsumed.current = true;
    setVideoUrl(incomingUrl);
    handleUrlUpload(incomingUrl);
    const next = new URLSearchParams(searchParams);
    next.delete("url");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Determine which video metadata to use and if modal should be open
  const currentVideoMetadata: VideoMetadata | null = useMemo(() => {
    // URL-based metadata takes precedence
    if (urlVideoMetadata) {
      return urlVideoMetadata;
    }

    // File-based metadata
    if (!blobUrl || !selectedFile) return null;

    // Use uploaded video URL if upload is complete, otherwise use blob URL for preview
    const videoUrl = isUploadComplete && uploadedVideoUrl ? uploadedVideoUrl : blobUrl;

    return {
      url: videoUrl,
      title: selectedFile.name.replace(/\.[^/.]+$/, ""), // Remove file extension
      duration: videoDuration, // Duration from Remotion
      platform: "http",
      thumbnail: undefined,
      best_video_format: {
        format_id: "upload",
        ext: "mp4",
        url: blobUrl,
        has_audio: true,
        has_video: true
      },
      best_audio_format: undefined
    };
  }, [urlVideoMetadata, blobUrl, selectedFile, videoDuration, isUploadComplete, uploadedVideoUrl]);

  // Determine if modal should be open
  const shouldShowModal = useMemo(() => {
    return !!urlVideoMetadata || isUploadModalOpen;
  }, [urlVideoMetadata, isUploadModalOpen]);

  // Determine if upload is complete for the current flow
  const currentIsUploadComplete = useMemo(() => {
    return urlVideoMetadata ? true : isUploadComplete;
  }, [urlVideoMetadata, isUploadComplete]);

  return (
    <div className="relative min-h-[100dvh] bg-background p-4 lg:p-6">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-80"
        style={{ background: 'radial-gradient(ellipse 70% 280px at 50% 0%, hsl(var(--primary) / 0.09) 0%, transparent 100%)' }}
      />
      <div
        data-testid="projects-page-stack"
        className={`relative w-full max-w-screen-2xl mx-auto ${isFirstClip ? 'flex min-h-[calc(100dvh-5.5rem)] flex-col justify-center py-4' : 'space-y-5 lg:space-y-6'}`}
      >
        {permissions.canCreateProjects ? (
          <>
            <LibraryCreateHero
              videoUrl={videoUrl}
              isLoadingMetadata={isLoadingMetadata}
              isFirstClip={isFirstClip}
              compact={!isFirstClip}
              copyPrefix="projects"
              communityVariant="quiet"
              headingId="page-title"
              onVideoUrlChange={setVideoUrl}
              onGenerate={handleGenerate}
              onUploadFile={handleFileSelectionClick}
            >
              {hasNewsPdfFeature && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={() => setIsNewsPdfModalOpen(true)}
                >
                  <FileText className="h-4 w-4" />
                  {t('projects.newsMode')}
                </Button>
              )}
            </LibraryCreateHero>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp4,video/mp4"
              className="hidden"
              onChange={handleFileSelection}
            />
          </>
        ) : (
          <div className="text-center">
            <h1 id="page-title" className="text-xl sm:text-2xl font-bold text-foreground">
              {t('projects.titleViewOnly')}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('projects.subtitleViewOnly')}
            </p>
          </div>
        )}

        {(!permissions.canCreateProjects || !isFirstClip) && (
        <div className="space-y-4">
          <h2 id="project-history-title" className="text-xl sm:text-2xl font-bold">{t('projects.projectHistory')}</h2>

          <div id="project-history" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading && Array.from({ length: 8 }).map((_, idx) => (
              <Card key={`skeleton-${idx}`} className="p-3 sm:p-4 animate-pulse">
                <div className="aspect-video bg-muted rounded-lg mb-3" />
                <div className="h-3 bg-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-muted rounded w-full mb-1" />
                <div className="h-3 bg-muted rounded w-2/3" />
              </Card>
            ))}

            {!loading && !error && projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onDuplicate={setDuplicateProject}
              />
            ))}

            {!loading && !error && projects.length === 0 && (
              <div className="col-span-full text-center text-sm text-muted-foreground">
                {t('projects.noProjectsFound')}
              </div>
            )}
            {error && (
              <div className="col-span-full text-center text-sm text-destructive">
                {t('projects.failedToLoadProjects')}
              </div>
            )}
            {isFetchingMore && (
              <div className="col-span-full text-center text-xs text-muted-foreground py-2">{t('projects.loadingMore')}</div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={shouldShowModal}
        onClose={handleModalClose}
        onUploadComplete={handleCreateProject}
        videoMetadata={currentVideoMetadata}
        uploadProgress={uploadProgress}
        isUploadComplete={currentIsUploadComplete}
        isUploaded={isUploaded}
      />

      {/* News PDF Upload Modal */}
      <NewsPdfUploadModal
        isOpen={isNewsPdfModalOpen}
        onClose={() => setIsNewsPdfModalOpen(false)}
      />

      {/* Duplicate Project Dialog */}
      <DuplicateProjectDialog
        isOpen={!!duplicateProject}
        onClose={() => setDuplicateProject(null)}
        project={duplicateProject}
      />
      {quotaUpsellDialog}
    </div>
  );
};

export default ProjectsListPage;
